import { writable, derived } from 'svelte/store';

const CART_KEY = 'others-cart';

function loadCart() {
  try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; }
  catch { return []; }
}

/** How many of this size/colour can be bought right now, from the product's own stock (per variant when it has them). */
export function availableStock(product, size = '', color = '') {
  if (!product) return Infinity;
  if (Array.isArray(product.variants) && product.variants.length) {
    const v = product.variants.find(v => (v.size || '') === (size || '') && (v.color || '') === (color || ''));
    return Math.max(0, Number(v?.stock) || 0);
  }
  return Math.max(0, Number(product.stock) || 0);
}

function createCart() {
  const { subscribe, set, update } = writable(loadCart());

  subscribe(items => {
    try { localStorage.setItem(CART_KEY, JSON.stringify(items)); } catch {}
  });

  return {
    subscribe,

    /** Adds items, never more than are in stock. Returns { added, max, capped } so the page can say so. */
    addItem(product, size = '', color = '', quantity = 1) {
      const max = availableStock(product, size, color);
      const key = `${product.id}-${size}-${color}`;
      let result = { added: 0, max, capped: false };
      update(items => {
        const existing = items.find(i => i.key === key);
        const have = existing ? existing.quantity : 0;
        const take = Math.max(0, Math.min(quantity, max - have));
        result = { added: take, max, capped: take < quantity };
        if (take === 0) return items;
        if (existing) {
          return items.map(i => i.key === key ? { ...i, quantity: i.quantity + take, max } : i);
        }
        return [...items, {
          key,
          productId: product.id,
          name: product.name,
          image: product.image,
          price: product.price,
          size,
          color,
          quantity: take,
          max,
        }];
      });
      return result;
    },

    removeItem(key) {
      update(items => items.filter(i => i.key !== key));
    },

    updateQuantity(key, quantity) {
      if (quantity < 1) return this.removeItem(key);
      update(items => items.map(i => i.key === key ? { ...i, quantity: i.max > 0 ? Math.min(quantity, i.max) : quantity } : i));
    },

    /**
     * Brings every line in line with what is in stock now (stock moves while an item sits in a cart): refreshes the limit, lowers a
     * quantity that is too high and removes what has sold out. Returns what changed so the cart page can tell the shopper.
     */
    syncStock(products) {
      if (!Array.isArray(products) || products.length === 0) return [];     // no data is not "everything is gone"
      const byId = new Map((products || []).map(p => [p.id, p]));
      const changes = [];
      update(items => items.flatMap(i => {
        const p = byId.get(i.productId);
        // Not in the shop any more (deleted, hidden, held for a drop): it cannot be bought, so it cannot stay in the cart.
        if (!p) { changes.push({ name: i.name, was: i.quantity, now: 0, gone: true }); return []; }
        const max = availableStock(p, i.size, i.color);
        if (max <= 0) { changes.push({ name: i.name, was: i.quantity, now: 0 }); return []; }
        if (i.quantity > max) { changes.push({ name: i.name, was: i.quantity, now: max }); return [{ ...i, quantity: max, max }]; }
        return [{ ...i, max }];
      }));
      return changes;
    },

    /** Brings stored prices in line with the store's current ones (prices can change while an item sits in a cart). */
    syncPrices(products) {
      const byId = new Map((products || []).map(p => [p.id, p.price]));
      update(items => items.map(i => (byId.has(i.productId) && byId.get(i.productId) !== i.price ? { ...i, price: byId.get(i.productId) } : i)));
    },

    /** Applies the server's price corrections ({ key, now }) after a checkout was refused for price drift. */
    applyPriceChanges(changes) {
      const now = new Map((changes || []).map(c => [c.key, c.now]));
      update(items => items.map(i => (now.has(i.key) ? { ...i, price: now.get(i.key) } : i)));
    },

    clear() { set([]); },
  };
}

export const cart = createCart();

export const cartCount = derived(cart, $cart =>
  $cart.reduce((sum, i) => sum + i.quantity, 0)
);

export const cartTotal = derived(cart, $cart =>
  $cart.reduce((sum, i) => sum + i.price * i.quantity, 0)
);
